import { GroupHierarchy, GroupMapping } from '@/models/groups/hierarchy';

import {
    GroupsReport,
    ReportContext,
    ReportData,
    ReportDataCoordinate,
    ReportOverallStats,
    ReportDataDrilldown,
    StruggleData,
    StruggleCompetency,
    StruggleGradeData,
    StruggleGradeCompetency,
} from '@/models/groups/report';
import {
    GroupsPerformance,
    PerformanceContext,
    PerformanceData,
    PerformanceOverallStats,
    PerformanceDrilldown,
    PerformanceCoOridnate,
} from '@/models/groups/performance';
import {
    GroupsTimespent,
    TimespentContext,
    TimespentData,
    TimespentCoOridnate,
    TimespentDrilldown,
    TimespentOverallStats,
} from '@/models/groups/timespent';
import { GroupCountry, CountryContext, CountryData, CountryOverallStats } from '@/models/groups/country';
import { SubjectContext, SubjectModel, SubjectsModel } from '@/models/groups/subjects';
import { UsersData, UsersDrilldown, UsersModel, UsersStats } from '@/models/groups/users';
import { sortByProperty } from '@/utils/utils';
import { DEFAULT_CLASS_DRILLDOWN_STATE } from '@/utils/constants';
import { appConfigService } from '@/providers/services/app/app-config';

class GroupsSerializer {
    public static instance = new GroupsSerializer();

    public serializeGroupCountries(payload: any) {
        const serializedGroupContries: GroupCountry | any = {};
        if (payload && payload.context) {
            const serializedCountryContext: CountryContext = {
                report: payload.context.report,
            };
            serializedGroupContries.context = serializedCountryContext;
        }

        if (payload && payload.data.length) {
            serializedGroupContries.data = payload.data.map((countryData: any) => {
                const normalizedCountryData: CountryData = {
                    id: countryData.id,
                    name: countryData.name,
                    code: countryData.code,
                    type: countryData.type,
                    overallStats: this.normalizeGroupCountryOverallstats(countryData.overall_stats),
                    hierarchies: countryData.hierarchies
                        ? this.normalizeGroupCountryHierarcies(countryData.hierarchies) : [],
                };
                return normalizedCountryData;
            });
        }
        return serializedGroupContries;
    }

    public normalizeACLDemoList(payload: any) {
        const aclPayload = payload.ACL ? payload.ACL : [];
        const acl: any = {
            aclByType: {},
            aclList: aclPayload,
        };
        acl.aclByType = aclPayload.reduce((r: any, a: any) => {
            r[a.type] = r[a.type] || [];
            r[a.type].push(a);
            return r;
        }, Object.create(null));
        return acl;
    }

    public serializeMappingData(payload: any) {
        const serializedGroupsMapping: GroupMapping[] = [];
        const results = payload && payload.results &&  payload.results.length > 0 ? payload.results : [];
        if (results &&  results.length > 0) {
            results.map((groupHierarchy: any) => {
                const serializedGroupsMap: GroupMapping = {
                    id: groupHierarchy.group.id,
                    name: groupHierarchy.group.name,
                    description: groupHierarchy.group.description,
                    code: groupHierarchy.group.code,
                    parentId: groupHierarchy.group.parent_id,
                    reference_id: groupHierarchy.group.reference_id,
                    type: groupHierarchy.group.type,
                };
                serializedGroupsMapping.push(serializedGroupsMap);
            });
        }
        return serializedGroupsMapping;
    }

    public serializeGrade(payload: any) {
        const serializedGrades: any = [];
        const grades = payload.grades;
        grades.map((item: any) => {
            const serializedGradesObj = {
                id: item.gradeId,
                name: item.gradeId,
                type: 'grade',
                subjects: item.subjects,
            };
            serializedGrades.push(serializedGradesObj);
        });
        return serializedGrades;
    }

    public serializeGroupedHierarchyMapping(payload: any) {
        const serializedGroupsMapping: GroupMapping[] = [];
        const results = payload && payload.results && payload.results.length > 0 ? payload.results : [];
        for (const groupHierarchy of results) {
            const serializedGroupsMap: GroupMapping = {
                id: groupHierarchy.id,
                name: groupHierarchy.name,
                description: groupHierarchy.description,
                code: groupHierarchy.code,
                parentId: groupHierarchy.parent_id,
                reference_id: groupHierarchy.reference_id,
                type: groupHierarchy.type,
            };
            serializedGroupsMapping.push(serializedGroupsMap);
        }
        const emptyList = serializedGroupsMapping.reduce((r: any, a: any) => {
            r[a.parentId] = r[a.parentId] || { selectedCohort: [], cohortList: [] };
            r[a.parentId].cohortList.push(a);
            return r;
        }, Object.create(null));
        return emptyList;
    }
    /**
     * @function serializeUserHierarchies
     * @param payload
     * @returns {GroupHierarchy[]}
     * Method to serializer groups hierarchies data
     */
    public serializeUserHierarchies(payload: any): GroupHierarchy[] {
        const serializedGroupsHierarchies: GroupHierarchy[] = [];
        if (payload && payload.hierarchies) {
            const groupHierarchies = payload.hierarchies || [];
            groupHierarchies.map((groupHierarchy: any) => {
                const serializedGroupsHierarchy: GroupHierarchy = {
                    id: groupHierarchy.id,
                    name: groupHierarchy.name,
                    description: groupHierarchy.description,
                    tenants: groupHierarchy.tenants,
                    levels: groupHierarchy.levels,
                };
                serializedGroupsHierarchies.push(serializedGroupsHierarchy);
            });
        }
        return serializedGroupsHierarchies;
    }

    public serializeGroupsReport(payload: any) {
        const serializedGroupsReport: GroupsReport | any = {};
        if (payload) {
            if (payload.context) {
                const reportContext: ReportContext = {
                    report: payload.context.report,
                    hierarchy: payload.context.hierarchy,
                    tenants: payload.context.tenants,
                    groupId: payload.context.group_id,
                    groupType: payload.context.group_type,
                    month: payload.context.month,
                    year: payload.context.year,
                    frequency: payload.context.frequency,
                };
                serializedGroupsReport.context = reportContext;
            }

            if (payload.data) {
                serializedGroupsReport.data = payload.data.map((reportData: any) => {
                    if (serializedGroupsReport.context.groupType === 'school' &&
                        payload.context.month === 2 && payload.context.year === 2020) {
                        reportData.drilldown = this.demoStubData(reportData.drilldown);
                    }
                    const serializedReportData: ReportData = {
                        id: reportData.id,
                        name: reportData.name,
                        code: reportData.code,
                        type: reportData.type,
                        overallStats: this.normalizeReportOverallStats(reportData.overall_stats),
                        coordinates: this.normalizeReportCoordinates(reportData.coordinates),
                        drilldown: this.normalizeReportDrilldown(reportData.drilldown),
                    };
                    return serializedReportData;
                });
            }
        }
        return serializedGroupsReport;
    }

    public serializeGroupPerformance(payload: any): GroupsPerformance {
        const serializedGroupsPerformance: GroupsPerformance | any = {};
        if (payload) {
            if (payload.context) {
                const performanceContext: PerformanceContext = {
                    report: payload.context.report,
                    hierarchy: payload.context.hierarchy,
                    tenants: payload.context.tenants,
                    groupId: payload.context.group_id,
                    groupType: payload.context.group_type,
                    month: payload.context.month,
                    year: payload.context.year,
                    frequency: payload.context.frequency,
                };
                serializedGroupsPerformance.context = performanceContext;
            }

            if (payload.data) {
                serializedGroupsPerformance.data = payload.data.map((payloadData: any) => {
                    if (serializedGroupsPerformance.context.groupType === 'school' &&
                        payload.context.month === 2 && payload.context.year === 2020) {
                        payloadData.drilldown = this.demoStubData(payloadData.drilldown);
                    }
                    const performanceData: PerformanceData = {
                        id: payloadData.id,
                        name: payloadData.name,
                        code: payloadData.code,
                        type: payloadData.type,
                        overallStats: this.normalizePerformanceOverallStats(payloadData.overall_stats),
                        coordinates: this.normalizePerformanceCoOrdinates(payloadData.coordinates),
                        drilldown: this.normalizePerformanceDrilldown(payloadData.drilldown),
                    };
                    return performanceData;
                });
            }
        }
        return serializedGroupsPerformance;
    }

    public serializeGroupTimespent(payload: any) {
        const serializedGroupsTimespent: GroupsTimespent | any = {};
        if (payload) {
            if (payload.context) {
                const timespentontext: TimespentContext = {
                    framework: payload.context.framework,
                    report: payload.context.report,
                    hierarchy: payload.context.hierarchy,
                    tenants: payload.context.tenants,
                    groupId: payload.context.group_id,
                    groupType: payload.context.group_type,
                    month: payload.context.month,
                    year: payload.context.year,
                    frequency: payload.context.frequency,
                };
                serializedGroupsTimespent.context = timespentontext;
            }

            if (payload.data) {
                serializedGroupsTimespent.data = payload.data.map((payloadData: any) => {
                    if (serializedGroupsTimespent.context.groupType === 'school' &&
                        payload.context.month === 2 && payload.context.year === 2020) {
                        payloadData.drilldown = this.demoStubData(payloadData.drilldown);
                    }
                    const timespentData: TimespentData = {
                        id: payloadData.id,
                        name: payloadData.name,
                        code: payloadData.code,
                        type: payloadData.type,
                        overallStats: this.normalizeTimespentOverallStats(payloadData.overall_stats),
                        coordinates: this.normalizeTimespentCoOrdinates(payloadData.coordinates),
                        drilldown: this.normalizeTimespentDrilldown(payloadData.drilldown),
                    };
                    return timespentData;
                });
            }
        }
        return serializedGroupsTimespent;
    }

    public groupsSubjectList(payload: any): SubjectsModel {
        const contextPayload = payload.context ? payload.context : null;
        const dataPayload = payload.data ? payload.data : [];
        return {
            context: this.subjectContext(contextPayload),
            data: this.subjectList(dataPayload),
        };
    }


    public normalizeUsers(payload: any) {
        const userDetails: UsersModel = {} as UsersModel;
        if (payload.context) {
            userDetails.context = this.subjectContext(payload.context);
        }
        const userDatas: UsersData[] = [];
        if (payload.data && payload.data.length) {
            payload.data.forEach((item: any) => {
                let usersStats: UsersStats = {} as UsersStats;
                if (item.overall_stats) {
                    usersStats = {
                        totalUsers: item.overall_stats.total_users || 0,
                        totalStudents: item.overall_stats.total_students || 0,
                        totalTeachers: item.overall_stats.total_teachers || 0,
                        totalOthers: item.overall_stats.total_others || 0,
                    };
                }

                const userData: UsersData = {
                    id: item.id,
                    name: item.name,
                    type: item.type,
                    code: item.code,
                    overallStats: usersStats,
                    coordinates: item.coordinates,
                    drilldown: this.usersDrilldown(item.drilldown),
                };
                userDatas.push(userData);
            });
        }
        userDetails.data = userDatas;
        return userDetails;
    }

    public normalizeStrugglingCompetency(payload: any) {
        const contextPayload: any = payload.context ? payload.context : {};
        const dataPayload: any = payload.data ? payload.data : [];

        const data: StruggleData[] = [];
        const context: ReportContext = {
            report: contextPayload.report || null,
            hierarchy: contextPayload.hierarchy || null,
            tenants: contextPayload.tenants || [],
            groupId: contextPayload.group_id || null,
            groupType: contextPayload.group_type || null,
            week: contextPayload.week || null,
            month: contextPayload.month || null,
            year: contextPayload.year || null,
            duration: contextPayload.duration || null,
            subject: contextPayload.subject || null,
            framework: contextPayload.framework || null,
        };
        dataPayload.forEach((item: any) => {
            const struggleItem: StruggleData = {
                gradeId: item.grade_id,
                grade: item.grade,
                gradeSeq: item.grade_seq,
                description: item.description,
                competencies: this.normalizeStruggleComp(item.competencies),
            };
            data.push(struggleItem);
        });
        sortByProperty(data, 'gradeSeq', 'ASC');
        return {
            context,
            data,
        };
    }

    public normalizeStrugglingGrades(payload: any) {
        const dataPayload: any = payload.struggling_competencies ? payload.struggling_competencies : [];

        const data: StruggleGradeData[] = [];
        dataPayload.forEach((item: any) => {
            const struggleItem: StruggleGradeData = {
                gradeId: item.grade_id,
                grade: item.grade,
                gradeSeq: item.grade_Seq,
                description: item.description,
                fwCode: item.fw_code,
                competencies: this.normalizeStruggleDomain(item.domains),
            };
            data.push(struggleItem);
        });
        sortByProperty(data, 'gradeSeq', 'ASC');
        return {
            data,
        };
    }

    public serializeClassData(data: any) {
        const item = data.classes;
        const classData: any = [];
        item.forEach((element: any) => {
            const dataClass = {
                id: element.classId,
                type: 'class',
                name: element.title,
            };
            classData.push(dataClass);
        });
        return classData;
    }

    private normalizeReportOverallStats(overallStats: any = {}): ReportOverallStats {
        return {
            totalCompletedCompetencies: overallStats ? overallStats.total_completed_competencies : 0,
            totalInferredCompetencies: overallStats ? overallStats.total_inferred_competencies : 0,
            overallMasteredCompetencies: overallStats ?
                (overallStats.total_completed_competencies || 0) +
                (overallStats.total_inferred_competencies || 0) +
                (overallStats.total_diagnostic_completed_competencies || 0) +
                (overallStats.total_diagnostic_inferred_competencies || 0) : 0,
            totalInprogressCompetencies: overallStats ?
                overallStats.total_inprogress_competencies :
                0,
            totalNotstartedCompetencies: overallStats ? overallStats.total_notstarted_competencies : 0,
            totalSkippedInferredComptencies: overallStats ? overallStats.total_skipped_inferred_competencies : 0,
            totalCompetencies: overallStats ?
                (overallStats.total_notstarted_competencies || 0) +
                (overallStats.total_inprogress_competencies || 0) +
                (overallStats.total_inferred_competencies || 0) +
                (overallStats.total_completed_competencies || 0) +
                (overallStats.total_diagnostic_completed_competencies || 0) +
                (overallStats.total_diagnostic_inferred_competencies || 0) : 0,
            totalDiagnosticCompetencies: overallStats ? overallStats.total_diagnostic_completed_competencies : 0,
            totalDiagnosticInferredCompetencies: overallStats ? overallStats.total_diagnostic_inferred_competencies : 0,
        };
    }

    private normalizeReportCoordinates(reportCoordinates: any | []): ReportDataCoordinate[] {
        let reportCoordinateItems = reportCoordinates.map((reportCoordinate: any) => {
            const normalizedCoordinate: ReportDataCoordinate = {
                coordinate: reportCoordinate.coordinate,
                label: reportCoordinate.label,
                completedCompetencies: reportCoordinate.completed_competencies,
                inferredCompetencies: reportCoordinate.inferred_competencies,
                inprogressCompetencies: reportCoordinate.inprogress_competencies,
                notstartedCompetencies: reportCoordinate.notstarted_competencies,
                skippedInferredCompetencies: reportCoordinate.skipped_inferred_competencies,
                masteredCompetencies: reportCoordinate.completed_competencies +
                    reportCoordinate.inferred_competencies +
                    reportCoordinate.diagnostic_completed_competencies +
                    reportCoordinate.diagnostic_inferred_competencies,
                diagnosticCompetencies: reportCoordinate.diagnostic_completed_competencies,
                diagnosticInferredCompetencies: reportCoordinate.diagnostic_inferred_competencies,
            };
            return normalizedCoordinate;
        });
        reportCoordinateItems = reportCoordinateItems.sort((a: any, b: any) => a.coordinate - b.coordinate);
        return reportCoordinateItems;
    }

    private normalizeReportDrilldown(reportDataDrilldown: any | []): ReportDataDrilldown[] {
        return reportDataDrilldown.map((dataDrilldown: any) => {
            const normalizedReportDrilldown: ReportDataDrilldown = {
                id: dataDrilldown.id,
                name: dataDrilldown.name,
                code: dataDrilldown.code,
                type: dataDrilldown.type,
                completedCompetencies: dataDrilldown.completed_competencies || 0,
                inferredCompetencies: dataDrilldown.inferred_competencies || 0,
                masteredCompetencies: (dataDrilldown.completed_competencies || 0) +
                    (dataDrilldown.inferred_competencies || 0) +
                    (dataDrilldown.diagnostic_completed_competencies || 0) +
                    (dataDrilldown.diagnostic_inferred_competencies || 0),
                inprogressCompetencies: dataDrilldown.inprogress_competencies || 0,
                notstartedCompetencies: dataDrilldown.notstarted_competencies || 0,
                skippedInferredCompetencies: dataDrilldown.skipped_inferred_competencies || 0,
                totalCompetencies: (dataDrilldown.completed_competencies || 0) +
                    (dataDrilldown.inferred_competencies || 0) +
                    (dataDrilldown.inprogress_competencies || 0) +
                    (dataDrilldown.notstarted_competencies || 0) +
                    (dataDrilldown.diagnostic_completed_competencies || 0) +
                    (dataDrilldown.diagnostic_inferred_competencies || 0),
                diagnosticCompetencies: dataDrilldown ? dataDrilldown.diagnostic_completed_competencies : 0,
                diagnosticInferredCompetencies: dataDrilldown ? dataDrilldown.diagnostic_inferred_competencies : 0,
            };
            if (normalizedReportDrilldown.type === 'class') {
                normalizedReportDrilldown.first_name = dataDrilldown.first_name;
                normalizedReportDrilldown.last_name = dataDrilldown.last_name;
                normalizedReportDrilldown.isArchived = dataDrilldown.is_archived;
            }
            return normalizedReportDrilldown;
        });
    }

    private normalizePerformanceOverallStats(overallStats: any): PerformanceOverallStats {
        overallStats = overallStats || {};
        const normalizedPerformanceOverallStats: PerformanceOverallStats = {
            averagePerformance: overallStats.average_performance === null ? -1 : overallStats.average_performance,
            totalCollectionTimespent: overallStats.total_collection_timespent || 0,
            totalAssessmentTimespent: overallStats.total_assessment_timespent || 0,
            averageCollectionTimespent: overallStats.average_collection_timespent || 0,
            averageAssessmentTimespent: overallStats.average_assessment_timespent || 0,
            totalTimespent: (overallStats.total_collection_timespent || 0)
                + (overallStats.total_assessment_timespent || 0),
        };
        return normalizedPerformanceOverallStats;
    }

    private normalizePerformanceDrilldown(drilldown: any[]): PerformanceDrilldown[] {
        const normalizedPerformanceDrilldownList: PerformanceDrilldown[] = drilldown.map((drilldownData) => {
            const normalizedPerformanceDrilldown: PerformanceDrilldown = {
                id: drilldownData.id,
                name: drilldownData.name,
                code: drilldownData.code,
                type: drilldownData.type,
                averagePerformance: drilldownData.average_performance === null ? -1 : drilldownData.average_performance,
                totalCollectionTimespent: drilldownData.total_collection_timespent || 0,
                totalAssessmentTimespent: drilldownData.total_assessment_timespent || 0,
                averageAssessmentTimespent: drilldownData.average_assessment_timespent || 0,
                averageCollectionTimespent: drilldownData.average_collection_timespent || 0,
            };
            return normalizedPerformanceDrilldown;
        });
        return normalizedPerformanceDrilldownList;
    }

    private normalizePerformanceCoOrdinates(coOrdinates: any[]): PerformanceCoOridnate[] {
        const normalizedPerformanceCoOrdinates: PerformanceCoOridnate[] = coOrdinates.map((coOrdinate) => {
            const normalizedPerfCoOrdinate: PerformanceCoOridnate = {
                label: coOrdinate.label,
                averagePerformance: coOrdinate.average_performance === null ? -1 : coOrdinate.average_performance,
            };
            return normalizedPerfCoOrdinate;
        });
        return normalizedPerformanceCoOrdinates;
    }

    private normalizeTimespentOverallStats(overallStats: any): TimespentOverallStats {
        overallStats = overallStats || {};
        const normalizedTimespentOverallStats: TimespentOverallStats = {
            activeStudents: overallStats.active_students || 0,
            activeStudentsIndex: overallStats.active_students_index || 0,
            totalStudents: overallStats.total_students || 0,
            totalCollectionTimespent: overallStats.total_collection_timespent || 0,
            totalAssessmentTimespent: overallStats.total_assessment_timespent || 0,
            totalOaTimespent: overallStats.total_oa_timespent || 0,
            averageCollectionTimespent: overallStats.average_collection_timespent || 0,
            averageAssessmentTimespent: overallStats.average_assessment_timespent || 0,
            averageOaTimespent: overallStats.average_oa_timespent || 0,
            overallAverageTimespent: overallStats.overall_average_timespent || 0,
        };
        return normalizedTimespentOverallStats;
    }

    private normalizeTimespentDrilldown(drilldown: any[]): TimespentDrilldown[] {
        const normalizedTimespentDrilldownList: TimespentDrilldown[] = drilldown.map((drilldownData) => {
            const normalizedTimespentDrilldown: TimespentDrilldown = {
                id: drilldownData.id,
                name: drilldownData.name,
                code: drilldownData.code,
                type: drilldownData.type,
                averageAssessmentTimespent: drilldownData.average_assessment_timespent || 0,
                averageCollectionTimespent: drilldownData.average_collection_timespent || 0,
                averageOaTimespent: drilldownData.average_oa_timespent || 0,
                overallAverageTimespent: drilldownData ?
                    (drilldownData.overall_average_timespent || 0) : 0,
                totalAssessmentTimespent: drilldownData.total_assessment_timespent || 0,
                totalCollectionTimespent: drilldownData.total_collection_timespent || 0,
                totalOaTimespent: drilldownData.total_oa_timespent || 0,
            };
            return normalizedTimespentDrilldown;
        });
        return normalizedTimespentDrilldownList;
    }

    private normalizeTimespentCoOrdinates(coOrdinates: any[]): TimespentCoOridnate[] {
        const normalizedTimespentCoOrdinates: TimespentCoOridnate[] = coOrdinates.map((coOrdinate) => {
            const normalizedTimespentCoOrdinate: TimespentCoOridnate = {
                label: coOrdinate.label,
                averageAssessmentTimespent: coOrdinate.average_assessment_timespent || 0,
                averageCollectionTimespent: coOrdinate.average_collection_timespent || 0,
                averageOaTimespent: coOrdinate.average_oa_timespent || 0,
                totalAssessmentTimespent: coOrdinate.total_assessment_timespent || 0,
                totalCollectionTimespent: coOrdinate.total_collection_timespent || 0,
                totalOaTimespent: coOrdinate.total_oa_timespent || 0,
            };
            return normalizedTimespentCoOrdinate;
        });
        return normalizedTimespentCoOrdinates;
    }

    private normalizeGroupCountryOverallstats(overallStats: any) {
        const normalizedOverallstats: CountryOverallStats = {
            totalCompletedCompetencies: overallStats ? overallStats.total_completed_competencies : 0,
            totalInferredCompetencies: overallStats ? overallStats.total_inferred_competencies : 0,
            totalInprogressCompetencies: overallStats ? overallStats.total_inprogress_competencies : 0,
            totalNotstartedComptenceis: overallStats ? overallStats.total_notstarted_competencies : 0,
            totalMasteredCompetencies: overallStats ?
                (overallStats.total_completed_competencies || 0) +
                (overallStats.total_inferred_competencies || 0) : 0,
            totalCompetencies: overallStats ?
                (overallStats.total_completed_competencies || 0) +
                (overallStats.total_inferred_competencies || 0) +
                (overallStats.total_inprogress_competencies || 0) +
                (overallStats.total_notstarted_competencies || 0) : 0,
        };
        return normalizedOverallstats;
    }

    private normalizeGroupCountryHierarcies(hierarchies: any[]) {
        return hierarchies.map((hierarcy: any) => {
            const normalizedHierarchy: GroupHierarchy = {
                id: hierarcy.id,
                name: hierarcy.name,
                description: hierarcy.description,
                tenants: hierarcy.tenants,
            };
            return normalizedHierarchy;
        });
    }

    private subjectContext(contextPayload: any): SubjectContext {
        return {
            report: contextPayload ? contextPayload.report : null,
            hierarchy: contextPayload ? contextPayload.hierarchy : null,
            tenants: contextPayload ? contextPayload.tenants : null,
            groupId: contextPayload ? contextPayload.group_id : null,
            week: contextPayload ? contextPayload.week : null,
            month: contextPayload ? contextPayload.month : null,
            year: contextPayload ? contextPayload.year : null,
            duration: contextPayload ? contextPayload.duration : null,
            groupType: contextPayload ? contextPayload.group_type : null,
        };
    }
    private subjectList(dataPayload: any): SubjectModel {
        return dataPayload.map((subject: any) => {
            return {
                code: subject.code,
                title: subject.title,
                framework: subject.framework,
                framework_title: subject.framework_title,
            };
        });
    }

    private usersDrilldown(payload: any) {
        const usersDrilldown: UsersDrilldown[] = [];
        if (payload && payload.length) {
            payload.forEach((item: any) => {
                const users: UsersDrilldown = {
                    id: item.id,
                    code: item.code,
                    name: item.name,
                    type: item.type,
                    totalUsers: item.total_users || 0,
                    totalStudents: item.total_students || 0,
                    totalTeachers: item.total_teachers || 0,
                    totalOthers: item.total_others || 0,
                };
                usersDrilldown.push(users);
            });
        }
        return usersDrilldown;
    }

    private normalizeStruggleComp(payload: any) {
        const competencies = payload ? payload : [];
        const competenciesList: StruggleCompetency[] = [];
        competencies.forEach((item: any) => {
            const competency: StruggleCompetency = {
                compCode: item.comp_code,
                compName: item.comp_name,
                compDisplayCode: item.comp_display_code,
                compSeq: item.comp_seq,
                domainCode: item.domain_code,
                domainName: item.domain_name,
                topicCode: item.topic_code,
                topicName: item.topic_name,
                studentCount: item.student_count,
                compStudentDesc: item.comp_student_desc,
            };
            competenciesList.push(competency);
        });
        return competenciesList;
    }

    private normalizeStruggleDomain(payload: any) {
        const domains = payload ? payload : [];
        const competencyList: StruggleGradeCompetency[] = [];
        domains.forEach((item: any) => {
            item.competencies.forEach((domainCompetency: any) => {
                const competency: StruggleGradeCompetency = {
                    compCode: domainCompetency.comp_code,
                    compName: domainCompetency.comp_name,
                    compDisplayCode: domainCompetency.comp_display_code,
                    compSeq: domainCompetency.comp_seq,
                    domainCode: item.domain_code,
                    domainName: item.domain_name,
                    studentCount: domainCompetency.student_count,
                    compStudentDesc: domainCompetency.comp_student_desc,
                };
                competencyList.push(competency);
            });
        });
        return competencyList;
    }
    /**
     * Only for demo purpose to made hard coded for the class level drilldown
     */
    private demoStubData(drilldowns: any) {
        if (appConfigService.isDemoTenant()) {
            const classDrilldown: any = DEFAULT_CLASS_DRILLDOWN_STATE;
            return drilldowns.map((drilldown: any) => {
                const defaultLevel = classDrilldown[drilldown.id];
                if (defaultLevel) {
                    drilldown.completed_competencies = defaultLevel.competency;
                    drilldown.inferred_competencies = 0;
                    drilldown.inprogress_competencies = defaultLevel.inprogress;
                    drilldown.skipped_inferred_competencies = 0;
                    drilldown.average_performance = defaultLevel.performance;
                    drilldown.average_assessment_timespent = defaultLevel.timespent;
                    drilldown.overall_average_timespent = defaultLevel.timespent;
                }
                return drilldown;
            });
        } else {
            return drilldowns;
        }
    }
}

export const groupsSerializer = GroupsSerializer.instance;
